/**
 * S3UploadContext.tsx
 * 
 * Context and reducer for managing S3 upload jobs with queue management,
 * pause/resume/cancel logic, and concurrency control (max 5 concurrent uploads).
 * 
 * Uses useReducer pattern to manage upload job state with a Map-based storage
 * for efficient O(1) job lookups and updates.
 */

import React from 'react';
import { createContext, useContext, useReducer, useCallback, useMemo, useEffect, type ReactNode } from 'react';
import type { UploadJob, UploadItem, UploadProgressMetrics, UploadStatus, UploadJobStatus, UploadOptions } from '../types/s3';
import { v4 as uuidv4 } from 'uuid';
import pLimit, { type LimitFunction } from 'p-limit';
import { ProgressAggregator, DEFAULT_AGGREGATION_CONFIG } from '../utils/progressAggregation';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * State shape for upload context - uses Map for efficient job management
 */
export interface UploadContextState {
  /** Map of upload jobs indexed by jobId for O(1) lookups */
  jobs: Map<string, UploadJob>;
  /** List of jobIds in queue order for FIFO processing */
  queue: string[];
  /** Number of currently active (uploading) jobs */
  activeJobCount: number;
  /** Maximum concurrent uploads allowed (default: 5) */
  maxConcurrent: number;
  /** Index in queue where processing is paused (for resuming from correct position) */
  pausedIndex: number;
}

/**
 * Union type for all possible reducer actions
 */
export type UploadAction = 
  | { type: 'START_UPLOAD'; payload: { job: UploadJob } }
  | { type: 'UPDATE_PROGRESS'; payload: { jobId: string; itemId: string; progress: UploadItem } }
  | { type: 'UPDATE_JOB_PROGRESS'; payload: { jobId: string; metrics: UploadProgressMetrics } }
  | { type: 'PAUSE_JOB'; payload: { jobId: string } }
  | { type: 'RESUME_JOB'; payload: { jobId: string } }
  | { type: 'CANCEL_JOB'; payload: { jobId: string } }
  | { type: 'RETRY_FILE'; payload: { jobId: string; itemId: string } }
  | { type: 'COMPLETE_JOB'; payload: { jobId: string; success: boolean } }
  | { type: 'MARK_ITEM_FAILED'; payload: { jobId: string; itemId: string; error: string } }
  | { type: 'MARK_ITEM_COMPLETED'; payload: { jobId: string; itemId: string } }
  | { type: 'CLEAR_COMPLETED_JOBS'; payload?: never };

/**
 * Context value interface for S3 upload context
 */
export interface S3UploadContextType {
  /** Current upload state */
  state: UploadContextState;
  /** Start a new upload job */
  startUpload: (files: File[], currentPath: string, options?: UploadOptions) => string;
  /** Update progress for an upload item */
  updateItemProgress: (jobId: string, itemId: string, progress: UploadItem) => void;
  /** Update overall job progress metrics */
  updateJobProgress: (jobId: string, metrics: UploadProgressMetrics) => void;
  /** Pause an upload job */
  pauseJob: (jobId: string) => void;
  /** Resume a paused upload job */
  resumeJob: (jobId: string) => void;
  /** Cancel an upload job */
  cancelJob: (jobId: string) => void;
  /** Retry a failed file upload */
  retryFile: (jobId: string, itemId: string) => void;
  /** Mark a job as completed */
  completeJob: (jobId: string, success: boolean) => void;
  /** Mark an item as failed */
  markItemFailed: (jobId: string, itemId: string, error: string) => void;
  /** Mark an item as completed */
  markItemCompleted: (jobId: string, itemId: string) => void;
  /** Get a specific job by ID */
  getJob: (jobId: string) => UploadJob | undefined;
  /** Get all active jobs */
  getActiveJobs: () => UploadJob[];
  /** Get queued jobs */
  getQueuedJobs: () => UploadJob[];
  /** Get number of currently active uploads */
  getActiveJobCount: () => number;
  /** Clear all completed jobs */
  clearCompletedJobs: () => void;
  /** Process a single upload job with concurrency control */
  processUploadJob: (jobId: string, uploadFn: (item: UploadItem) => Promise<void>) => Promise<void>;
  /** Process the entire upload queue with concurrency control and pause/resume support */
  processQueue: (uploadFn: (item: UploadItem) => Promise<void>) => Promise<void>;
}

// ============================================================================
// Reducer Implementation
// ============================================================================

/**
 * Calculate progress metrics for a job based on its files
 */
const calculateJobProgress = (job: UploadJob): UploadProgressMetrics => {
  const now = new Date();
  const filesUploaded = job.files.filter(f => f.status === 'completed').length;
  const filesFailed = job.files.filter(f => f.status === 'failed').length;
  const totalBytes = job.files.reduce((sum, f) => sum + f.file.size, 0);
  const bytesUploaded = job.files.reduce((sum, f) => sum + f.bytesUploaded, 0);

  const elapsedMs = now.getTime() - job.progress.startTime.getTime();
  const elapsedSeconds = elapsedMs / 1000;
  const uploadSpeed = elapsedSeconds > 0 ? Math.max(0, bytesUploaded / elapsedSeconds) : 0;

  let estimatedTimeRemaining: number | null = null;
  if (uploadSpeed > 0 && bytesUploaded < totalBytes) {
    estimatedTimeRemaining = Math.ceil((totalBytes - bytesUploaded) / uploadSpeed);
  }

  return {
    totalFiles: job.files.length,
    filesUploaded,
    filesFailed,
    totalBytes,
    bytesUploaded,
    uploadSpeed,
    estimatedTimeRemaining,
    percentage: totalBytes > 0 ? Math.min(100, Math.round((bytesUploaded / totalBytes) * 100)) : 0,
    startTime: job.progress.startTime,
    currentTime: now,
  };
};

/**
 * Create a new upload job with initial state
 */
const createUploadJob = (
  files: File[],
  currentPath: string,
  options?: UploadOptions
): UploadJob => {
  const jobId = uuidv4();
  const now = new Date();
  
  // Normalize currentPath by removing trailing slash
  const normalizedPath = currentPath ? currentPath.replace(/\/+$/, '') : '';
  
  // Create upload items for each file
  const uploadItems: UploadItem[] = files.map(file => {
    // Use webkitRelativePath for folder uploads to preserve structure,
    // otherwise use file.name for individual file uploads
    const relativePath = (file as any).webkitRelativePath || file.name;
    const fullKey = normalizedPath ? `${normalizedPath}/${relativePath}` : relativePath;
    
    return {
      id: uuidv4(),
      file,
      key: fullKey,
      status: 'pending' as UploadStatus,
      progress: 0,
      bytesUploaded: 0,
      uploadSpeed: 0,
      estimatedTimeRemaining: null,
      error: undefined,
      retryCount: 0,
      startTime: undefined,
      lastUpdated: now,
    };
  });

  // Calculate initial progress metrics
  const totalBytes = uploadItems.reduce((sum, item) => sum + item.file.size, 0);
  const progress: UploadProgressMetrics = {
    totalFiles: uploadItems.length,
    filesUploaded: 0,
    filesFailed: 0,
    totalBytes,
    bytesUploaded: 0,
    uploadSpeed: 0,
    estimatedTimeRemaining: null,
    percentage: 0,
    startTime: now,
    currentTime: now,
  };

  return {
    jobId,
    status: 'pending' as UploadJobStatus,
    files: uploadItems,
    progress,
    userId: undefined,
    isCancelled: false,
    isPaused: false,
    abortController: new AbortController(),
  };
};

/**
 * Upload job reducer function handling all action types
 */
export const uploadJobReducer = (
  state: UploadContextState,
  action: UploadAction
): UploadContextState => {
  switch (action.type) {
    case 'START_UPLOAD': {
      const { job } = action.payload;
      const newJobs = new Map(state.jobs);
      newJobs.set(job.jobId, { ...job, status: 'pending' });

      return {
        ...state,
        jobs: newJobs,
        queue: [...state.queue, job.jobId],
      };
    }

    case 'UPDATE_PROGRESS': {
      const { jobId, itemId, progress: updatedItem } = action.payload;
      const job = state.jobs.get(jobId);
      
      if (!job) return state;

      const updatedFiles = job.files.map(f =>
        f.id === itemId ? updatedItem : f
      );

      const updatedJob: UploadJob = {
        ...job,
        files: updatedFiles,
      };

      const newJobs = new Map(state.jobs);
      newJobs.set(jobId, updatedJob);

      return {
        ...state,
        jobs: newJobs,
      };
    }

    case 'UPDATE_JOB_PROGRESS': {
      const { jobId, metrics } = action.payload;
      const job = state.jobs.get(jobId);

      if (!job) return state;

      const updatedJob: UploadJob = {
        ...job,
        progress: metrics,
      };

      const newJobs = new Map(state.jobs);
      newJobs.set(jobId, updatedJob);

      return {
        ...state,
        jobs: newJobs,
      };
    }

    case 'PAUSE_JOB': {
      const { jobId } = action.payload;
      const job = state.jobs.get(jobId);

      if (!job) return state;

      const updatedJob: UploadJob = {
        ...job,
        status: 'paused',
        isPaused: true,
      };

      const newJobs = new Map(state.jobs);
      newJobs.set(jobId, updatedJob);

      return {
        ...state,
        jobs: newJobs,
        activeJobCount: Math.max(0, state.activeJobCount - 1),
      };
    }

    case 'RESUME_JOB': {
      const { jobId } = action.payload;
      const job = state.jobs.get(jobId);

      if (!job) return state;

      const updatedJob: UploadJob = {
        ...job,
        status: 'in-progress',
        isPaused: false,
      };

      const newJobs = new Map(state.jobs);
      newJobs.set(jobId, updatedJob);

      return {
        ...state,
        jobs: newJobs,
        activeJobCount: Math.min(state.activeJobCount + 1, state.maxConcurrent),
      };
    }

    case 'CANCEL_JOB': {
      const { jobId } = action.payload;
      const job = state.jobs.get(jobId);

      if (!job) return state;

      // Cancel all pending and uploading files
      const cancelledFiles = job.files.map(f =>
        f.status === 'pending' || f.status === 'uploading'
          ? { ...f, status: 'cancelled' as UploadStatus }
          : f
      );

      const updatedJob: UploadJob = {
        ...job,
        status: 'cancelled',
        files: cancelledFiles,
        isCancelled: true,
      };

      const newJobs = new Map(state.jobs);
      newJobs.set(jobId, updatedJob);

      return {
        ...state,
        jobs: newJobs,
        activeJobCount: Math.max(0, state.activeJobCount - 1),
      };
    }

    case 'RETRY_FILE': {
      const { jobId, itemId } = action.payload;
      const job = state.jobs.get(jobId);

      if (!job) return state;

      const updatedFiles = job.files.map(f =>
        f.id === itemId
          ? {
              ...f,
              status: 'pending' as UploadStatus,
              retryCount: f.retryCount + 1,
              error: undefined,
              progress: 0,
              bytesUploaded: 0,
            }
          : f
      );

      const updatedJob: UploadJob = {
        ...job,
        files: updatedFiles,
      };

      const newJobs = new Map(state.jobs);
      newJobs.set(jobId, updatedJob);

      return {
        ...state,
        jobs: newJobs,
      };
    }

    case 'COMPLETE_JOB': {
      const { jobId, success } = action.payload;
      const job = state.jobs.get(jobId);

      if (!job) return state;

      const updatedJob: UploadJob = {
        ...job,
        status: success ? 'completed' : 'failed',
      };

      const newJobs = new Map(state.jobs);
      newJobs.set(jobId, updatedJob);

      // Only decrement if job was active
      const wasActive = job.status === 'in-progress' && !job.isPaused;

      return {
        ...state,
        jobs: newJobs,
        activeJobCount: wasActive ? Math.max(0, state.activeJobCount - 1) : state.activeJobCount,
      };
    }

    case 'MARK_ITEM_FAILED': {
      const { jobId, itemId, error } = action.payload;
      const job = state.jobs.get(jobId);

      if (!job) return state;

      const updatedFiles = job.files.map(f =>
        f.id === itemId
          ? { ...f, status: 'failed' as UploadStatus, error }
          : f
      );

      const updatedJob: UploadJob = {
        ...job,
        files: updatedFiles,
      };

      const newJobs = new Map(state.jobs);
      newJobs.set(jobId, updatedJob);

      return {
        ...state,
        jobs: newJobs,
      };
    }

    case 'MARK_ITEM_COMPLETED': {
      const { jobId, itemId } = action.payload;
      const job = state.jobs.get(jobId);

      if (!job) return state;

      const updatedFiles = job.files.map(f =>
        f.id === itemId
          ? { ...f, status: 'completed' as UploadStatus }
          : f
      );

      const updatedJob: UploadJob = {
        ...job,
        files: updatedFiles,
      };

      const newJobs = new Map(state.jobs);
      newJobs.set(jobId, updatedJob);

      return {
        ...state,
        jobs: newJobs,
      };
    }

    case 'CLEAR_COMPLETED_JOBS': {
      const newJobs = new Map<string, UploadJob>();
      const newQueue: string[] = [];

      for (const [jobId, job] of state.jobs) {
        if (job.status !== 'completed' && job.status !== 'failed' && job.status !== 'cancelled') {
          newJobs.set(jobId, job);
          if (state.queue.includes(jobId)) {
            newQueue.push(jobId);
          }
        }
      }

      return {
        ...state,
        jobs: newJobs,
        queue: newQueue,
      };
    }

    default: {
      const _exhaustiveCheck: never = action;
      return _exhaustiveCheck;
    }
  }
};

// ============================================================================
// Context and Provider
// ============================================================================

const S3UploadContext = createContext<S3UploadContextType | null>(null);

/**
 * Custom hook to use S3 upload context
 */
export const useS3Upload = (): S3UploadContextType => {
  const context = useContext(S3UploadContext);
  if (!context) {
    throw new Error('useS3Upload must be used within an S3UploadProvider');
  }
  return context;
};

/**
 * Initial state for upload context
 */
const createInitialState = (maxConcurrent: number = 5): UploadContextState => ({
  jobs: new Map(),
  queue: [],
  activeJobCount: 0,
  maxConcurrent,
  pausedIndex: 0,
});

/**
 * Props for S3UploadProvider component
 */
export interface S3UploadProviderProps {
  /** Child components */
  children: ReactNode;
  /** Maximum concurrent uploads (default: 5) */
  maxConcurrent?: number;
}

/**
 * S3UploadProvider component
 * 
 * Provides upload context to child components with reducer-based state management
 * and concurrency control via p-limit
 */
export const S3UploadProvider: React.FC<S3UploadProviderProps> = ({
  children,
  maxConcurrent = 5,
}) => {
  const [state, dispatch] = useReducer(
    uploadJobReducer,
    createInitialState(maxConcurrent)
  );

  // Create a persistent concurrency limiter with p-limit
  // This limits the number of concurrent promises to maxConcurrent
  const limiter = useMemo<LimitFunction>(() => pLimit(maxConcurrent), [maxConcurrent]);

  // Set up progress aggregation to batch updates every 500ms
  useEffect(() => {
    const aggregator = new ProgressAggregator(
      (metrics: UploadProgressMetrics) => {
        // Update all active jobs with aggregated metrics
        // This prevents excessive state updates while keeping UI in sync
        for (const job of state.jobs.values()) {
          if (job.status === 'in-progress' && !job.isPaused) {
            dispatch({
              type: 'UPDATE_JOB_PROGRESS',
              payload: { jobId: job.jobId, metrics },
            });
          }
        }
      },
      () => state.jobs,
      DEFAULT_AGGREGATION_CONFIG
    );

    // Schedule an immediate aggregation update
    // This ensures progress is reflected in the UI
    aggregator.scheduleUpdate();

    // Schedule periodic updates while jobs are active
    const intervalId = setInterval(() => {
      const hasActiveJobs = Array.from(state.jobs.values()).some(
        job => job.status === 'in-progress' && !job.isPaused
      );

      if (hasActiveJobs) {
        aggregator.scheduleUpdate();
      }
    }, DEFAULT_AGGREGATION_CONFIG.aggregationIntervalMs);

    return () => {
      clearInterval(intervalId);
      aggregator.destroy();
    };
  }, [state]);

  // Dispatch callbacks for different actions
  const startUpload = useCallback(
    (files: File[], currentPath: string, options?: UploadOptions): string => {
      const job = createUploadJob(files, currentPath, options);
      dispatch({ type: 'START_UPLOAD', payload: { job } });
      return job.jobId;
    },
    []
  );

  const updateItemProgress = useCallback(
    (jobId: string, itemId: string, progress: UploadItem) => {
      dispatch({
        type: 'UPDATE_PROGRESS',
        payload: { jobId, itemId, progress },
      });
    },
    []
  );

  const updateJobProgress = useCallback(
    (jobId: string, metrics: UploadProgressMetrics) => {
      dispatch({
        type: 'UPDATE_JOB_PROGRESS',
        payload: { jobId, metrics },
      });
    },
    []
  );

  const pauseJob = useCallback((jobId: string) => {
    dispatch({ type: 'PAUSE_JOB', payload: { jobId } });
  }, []);

  const resumeJob = useCallback((jobId: string) => {
    dispatch({ type: 'RESUME_JOB', payload: { jobId } });
  }, []);

  const cancelJob = useCallback((jobId: string) => {
    dispatch({ type: 'CANCEL_JOB', payload: { jobId } });
  }, []);

  const retryFile = useCallback((jobId: string, itemId: string) => {
    dispatch({ type: 'RETRY_FILE', payload: { jobId, itemId } });
  }, []);

  const completeJob = useCallback((jobId: string, success: boolean) => {
    dispatch({ type: 'COMPLETE_JOB', payload: { jobId, success } });
  }, []);

  const markItemFailed = useCallback((jobId: string, itemId: string, error: string) => {
    dispatch({
      type: 'MARK_ITEM_FAILED',
      payload: { jobId, itemId, error },
    });
  }, []);

  const markItemCompleted = useCallback((jobId: string, itemId: string) => {
    dispatch({
      type: 'MARK_ITEM_COMPLETED',
      payload: { jobId, itemId },
    });
  }, []);

  const getJob = useCallback(
    (jobId: string): UploadJob | undefined => state.jobs.get(jobId),
    [state.jobs]
  );

  const getActiveJobs = useCallback((): UploadJob[] => {
    const active: UploadJob[] = [];
    for (const job of state.jobs.values()) {
      if (job.status === 'in-progress' && !job.isPaused) {
        active.push(job);
      }
    }
    return active;
  }, [state.jobs]);

  const getQueuedJobs = useCallback((): UploadJob[] => {
    return state.queue
      .map(jobId => state.jobs.get(jobId))
      .filter((job): job is UploadJob => job !== undefined);
  }, [state.jobs, state.queue]);

  const getActiveJobCount = useCallback((): number => state.activeJobCount, [state.activeJobCount]);

  const clearCompletedJobs = useCallback(() => {
    dispatch({ type: 'CLEAR_COMPLETED_JOBS' });
  }, []);

  /**
   * Process all jobs in the upload queue with concurrency control
   * Respects pause/resume state and handles individual job uploads
   */
  const processUploadJob = useCallback(
    async (jobId: string, uploadFn: (item: UploadItem) => Promise<void>) => {
      const job = state.jobs.get(jobId);
      if (!job) return;

      // Get pending files for this job
      const pendingItems = job.files.filter(f => f.status === 'pending');
      
      // Create array of upload tasks wrapped with concurrency limiter
      const uploadTasks = pendingItems.map(item =>
        limiter(async () => {
          // Check if job was cancelled or paused before starting
          const currentJob = state.jobs.get(jobId);
          if (!currentJob || currentJob.isCancelled || currentJob.isPaused) {
            return;
          }

          try {
            // Execute the upload function with the item
            await uploadFn(item);
          } catch (error) {
            // Error handling is done by the uploadFn caller
          }
        })
      );

      // Execute all tasks with concurrency control
      await Promise.all(uploadTasks);
    },
    [state.jobs, limiter]
  );

  /**
   * Process the entire upload queue sequentially with concurrency control for each job
   * Handles pause/resume with pausedIndex to avoid reprocessing
   */
  const processQueue = useCallback(
    async (uploadFn: (item: UploadItem) => Promise<void>) => {
      const { queue, pausedIndex } = state;
      
      // Process jobs starting from pausedIndex
      for (let i = pausedIndex; i < queue.length; i++) {
        const jobId = queue[i];
        const job = state.jobs.get(jobId);

        if (!job) continue;

        // Check for global pause/cancel before processing this job
        const currentJob = state.jobs.get(jobId);
        if (!currentJob) break;

        // If job is cancelled, skip it
        if (currentJob.isCancelled) continue;

        // If job is paused, stop processing queue and save position
        if (currentJob.isPaused) {
          // pausedIndex is tracked in state and will be used to resume
          break;
        }

        // Process this job with concurrency control
        await processUploadJob(jobId, uploadFn);

        // Check pause status after processing each job
        const jobAfterProcess = state.jobs.get(jobId);
        if (jobAfterProcess?.isPaused) {
          break;
        }
      }
    },
    [state, processUploadJob]
  );

  const contextValue: S3UploadContextType = useMemo(
    () => ({
      state,
      startUpload,
      updateItemProgress,
      updateJobProgress,
      pauseJob,
      resumeJob,
      cancelJob,
      retryFile,
      completeJob,
      markItemFailed,
      markItemCompleted,
      getJob,
      getActiveJobs,
      getQueuedJobs,
      getActiveJobCount,
      clearCompletedJobs,
      processUploadJob,
      processQueue,
    }),
    [
      state,
      startUpload,
      updateItemProgress,
      updateJobProgress,
      pauseJob,
      resumeJob,
      cancelJob,
      retryFile,
      completeJob,
      markItemFailed,
      markItemCompleted,
      getJob,
      getActiveJobs,
      getQueuedJobs,
      getActiveJobCount,
      clearCompletedJobs,
      processUploadJob,
      processQueue,
    ]
  );

  return (
    <S3UploadContext.Provider value={contextValue}>
      {children}
    </S3UploadContext.Provider>
  );
};

export default S3UploadContext;
